import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Plus, MessageSquare } from "lucide-react";
import { useState } from "react";

import { NoteDialog } from "@features/cx/shared/components/NoteDialog";
import { useNotes } from "@features/cx/shared/hooks/useNotes";


// Simplified props interface
export interface NotesTabProps {
  requestId: string;
  noteType?: string;
  remoteUtilityId: number;
  source: string;
}

export function NotesTab({ requestId, noteType, remoteUtilityId, source }: NotesTabProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Use the custom hook for API calls
  const { notes, createNote } = useNotes({
    requestId: Number(requestId),
    noteType: noteType,
    remoteUtilityId: remoteUtilityId,
    source: source,
  });

  const createNoteWrapper = (itemId: string) => ({
    mutateAsync: async (data: any) => {
      await createNote({
        source: source,
        requestId: Number(itemId),
        remoteUtilityId: Number(remoteUtilityId),
        notes: {
          note: data.notes.note,
          noteType: data.notes.noteType
        }
      });
    }
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg pb-4">Notes</CardTitle>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => setIsDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Note
        </Button>
      </CardHeader>
      <CardContent>
        {notes.length === 0 ? (
          <div className="text-center p-8">
            <MessageSquare className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <h3 className="text-lg font-medium text-gray-600">No Notes</h3>
            <p className="text-sm text-gray-500">No notes available for this request</p>
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note, index) => (
              <div 
                key={note.id || `note-${index}`} 
                className="p-4 border rounded-md bg-gray-50"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{note?.note ? note?.note: note.noteDescription}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{note.createdDate}</span>
                  </div>
                </div>
                <div className="mb-2">
                  <span className="text-sm text-gray-600">By: {note.createdBy ? note.createdBy : note.createdUserRemoteName ?? "NA" }</span>
                </div>
                {/* <p className="text-sm">{note.note}</p> */}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Add Note Dialog */}
      <NoteDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        itemId={requestId}
        remoteUtilityId={remoteUtilityId}
        useSendNote={createNoteWrapper}
      />
    </Card>
  );
}